import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Dialog, IconButton, Stack, Typography, TabContext, TabList, Tab} from 'views/components';
import {Close} from 'views/icons';
import {Translate} from 'views/module';

import PlaybookStepPrompt from './playbooks_new_components/steps/PlaybookStepPrompt';
import PlaybooksStepsPreview from './playbooks_new_components/steps/PlaybooksStepsPreview';

import PlaybookEngagementPrompt from './playbooks_new_components/engagement/PlaybookEngagementPrompt';
import PlaybooksEngagementPreview from './playbooks_new_components/engagement/PlaybookEngagementPreview';

import PlaybooksNewGroup from './playbooks_new_components/basic/PlaybooksNewGroup';
import PlaybooksNewName from './playbooks_new_components/basic/PlaybooksNewName';
import PlaybooksNewPrompt from './playbooks_new_components/basic/PlaybooksNewPrompt';

const PlaybookNew = (props) => {
  const {active, groupOptions, handleCreatePlaybook, onClose, playbook} = props;

  const hasExistingGroups = groupOptions.length > 0;
  const defaultGroupMode = hasExistingGroups ? 'existing' : 'new';
  const defaultExistingGroup = hasExistingGroups ? groupOptions[0] : '';

  const [groupMode, setGroupMode] = React.useState(defaultGroupMode);
  const [existingGroup, setExistingGroup] = React.useState(defaultExistingGroup);
  const [playbookTotalDays, setPlaybookTotalDays] = React.useState(1);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [playbookTitle, setPlaybookTitle] = React.useState('');
  const [playbookDescription, setPlaybookDescription] = React.useState('');
  const [steps, setSteps] = React.useState([]);
  const [stepPrompt, setStepPrompt] = React.useState('');
  const [rules, setRules] = React.useState([]);
  const [rulePrompt, setRulePrompt] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState('basic');

  const groupName = groupMode === 'existing' ? existingGroup : newGroupName.trim();
  const isEditing = Boolean(playbook?.playbookRn);
  const isBasicStepValid = Boolean(playbookTitle && groupName);
  const isStepsStepValid = steps.length > 0;
  const isSubmitDisabled = !(playbookTitle && groupName && steps.length > 0);

  const resetForm = React.useCallback(() => {
    setGroupMode(defaultGroupMode);
    setExistingGroup(defaultExistingGroup);
    setNewGroupName('');
    setPlaybookTitle('');
    setPlaybookDescription('');
    setSteps([]);
    setStepPrompt('');
    setRules([]);
    setRulePrompt('');
    setPlaybookTotalDays(1);
    setIsSubmitting(false);
  }, [defaultExistingGroup, defaultGroupMode]);

  const addStep = React.useCallback((step) => {
    setSteps((currentSteps) => {
      const lastMatchingDayIndex = currentSteps.reduce((lastIndex, currentStep, currentIndex) => {
        return currentStep.day === step.day ? currentIndex : lastIndex;
      }, -1);

      if (lastMatchingDayIndex !== -1) {
        return [
          ...currentSteps.slice(0, lastMatchingDayIndex + 1),
          step,
          ...currentSteps.slice(lastMatchingDayIndex + 1),
        ];
      }

      const nextDayIndex = currentSteps.findIndex((currentStep) => currentStep.day > step.day);

      if (nextDayIndex === -1) {
        return [
          ...currentSteps,
          step,
        ];
      }

      return [
        ...currentSteps.slice(0, nextDayIndex),
        step,
        ...currentSteps.slice(nextDayIndex),
      ];
    });
    setStepPrompt('');
  }, []);

  const addRule = React.useCallback((rule) => {
    setRules((currentRules) => [...currentRules, rule]);
    setRulePrompt('');
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (isSubmitDisabled) {
      return;
    }

    const playbookPayload = {
      title: playbookTitle,
      description: {
        blob: playbookDescription,
        editorType: 'TEXT',
      },
      groupName,
      steps,
      engagementRules: rules,
    };
    const existingPlaybookRn = playbook?.playbookRn || null;

    setIsSubmitting(true);

    try {
      await handleCreatePlaybook(playbookPayload, existingPlaybookRn);
    } finally {
      setIsSubmitting(false);
    }
  }, [groupName, handleCreatePlaybook, isSubmitDisabled, playbook, playbookDescription, playbookTitle, rules, steps]);

  const handlePrimaryAction = React.useCallback(async () => {
    if (isEditing) {
      await handleSubmit();
      return;
    }

    if (activeTab === 'basic') {
      if (!isBasicStepValid) {
        return;
      }

      setActiveTab('steps');
      return;
    }

    if (activeTab === 'steps') {
      if (!isStepsStepValid) {
        return;
      }

      setActiveTab('engagement');
      return;
    }

    await handleSubmit();
  }, [activeTab, handleSubmit, isBasicStepValid, isEditing, isStepsStepValid]);

  const primaryButtonDisabled = React.useMemo(() => {
    if (isSubmitting) {
      return true;
    }

    if (isEditing) {
      return isSubmitDisabled;
    }

    if (activeTab === 'basic') {
      return !isBasicStepValid;
    }

    if (activeTab === 'steps') {
      return !isStepsStepValid;
    }

    return isSubmitDisabled;
  }, [activeTab, isBasicStepValid, isEditing, isStepsStepValid, isSubmitDisabled, isSubmitting]);

  const primaryButtonLabel = isEditing ?
    'playbooks.New.SaveChanges' :
    activeTab === 'engagement' ?
      'playbooks.New.Create' :
      'playbooks.New.NextStep';

  React.useEffect(() => {
    if (!active) {
      resetForm();
      return;
    }

    if (playbook) {
      const playbookSteps = playbook.steps;
      const normalizedSteps =
        [...playbookSteps].sort((leftStep, rightStep) => leftStep.day - rightStep.day);
      const highestStepDay = normalizedSteps.reduce((maxDay, step) => {
        const stepDay = step?.day || 1;

        return Math.max(maxDay, stepDay);
      }, 1);

      setGroupMode('existing');
      setExistingGroup(playbook.groupName);
      setNewGroupName('');
      setPlaybookTitle(playbook.title);
      setPlaybookDescription(playbook.description.blob);
      setSteps(normalizedSteps);
      setStepPrompt('');
      setRules(playbook.engagementRules || []);
      setRulePrompt('');
      setPlaybookTotalDays(highestStepDay);
      return;
    }

    resetForm();
  }, [active, playbook, resetForm]);

  if (!active) {
    return null;
  }

  return (
    <Dialog
      onClose={onClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(26, 25, 22, 0.28)',
        },
      }}
      PaperProps={{
        sx: {
          width: 'calc(100vw - 24px)',
          maxWidth: '692px',
          maxHeight: 'calc(100vh - 24px)',
          m: '12px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '22px',
          border: '1px solid #EDE8E2',
          boxShadow: '0px 28px 90px rgba(26, 25, 22, 0.16)',
          overflow: 'visible',
          backgroundColor: '#FFFFFF',
        },
      }}
    >
      <Stack
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Stack
          sx={{
            px: {
              xs: '20px',
              sm: '26px',
            },
            pt: '16px',
            pb: 0,
            gap: '10px',
            flexShrink: 0,
          }}
        >
          <Stack
            direction='row'
            alignItems='flex-start'
            justifyContent='space-between'
            sx={{
              gap: '16px',
            }}
          >
            <Stack sx={{gap: '10px'}}>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 400,
                  color: '#A8A49E',
                  lineHeight: '100%',
                }}
              >
                <Translate expression='playbooks.New.Eyebrow' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  fontWeight: 500,
                  color: '#1A1916',
                  lineHeight: '120%',
                }}
              >
                <Translate expression={isEditing ? 'playbooks.New.EditTitle' : 'playbooks.New.Title'} />
              </Typography>
            </Stack>
            <IconButton
              onClick={onClose}
              sx={{
                'width': '28px',
                'height': '28px',
                'borderRadius': '7px',
                'border': '1px solid #E8E4DE',
                'color': '#B0AAA2',
                'backgroundColor': '#FFFFFF',
                'flexShrink': 0,
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  borderColor: '#E8E4DE',
                },
              }}
            >
              <Close
                sx={{
                  fontSize: '16px',
                }}
              />
            </IconButton>
          </Stack>
          <TabContext value={activeTab}>
            <TabList
              onChange={(_event, value) => setActiveTab(value)}
              textColor='inherit'
              sx={{
                'minHeight': 'auto',
                'mb': 0,
                'width': 'calc(100% + 52px)',
                'mx': {
                  xs: '-20px',
                  sm: '-26px',
                },
                'px': {
                  xs: '20px',
                  sm: '26px',
                },
                'borderBottom': '1px solid #EEE8E2',
                '& .MuiTabs-flexContainer': {
                  p: 0,
                  m: 0,
                  gap: 0,
                },
                '& .MuiTab-root': {
                  minHeight: 'auto',
                  minWidth: '4rem',
                  p: 1,
                  m: 0,
                  color: '#8E8A84',
                  textTransform: 'none',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#1A1916',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#DA7955',
                },
              }}
            >
              <Tab
                label={<Translate expression='playbooks.New.Tabs.Basic' />}
                value='basic'
              />
              <Tab
                label={<Translate expression='playbooks.New.Tabs.Steps' />}
                value='steps'
              />
              <Tab
                label={<Translate expression='playbooks.New.Tabs.Engagement' />}
                value='engagement'
              />
            </TabList>
          </TabContext>
        </Stack>

        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: activeTab === 'basic' ? 'auto' : 'visible',
            px: {
              xs: '20px',
              sm: '26px',
            },
            pt: 0,
            pb: '18px',
            gap: '16px',
          }}
        >
          {
            activeTab === 'basic' &&
            <Stack sx={{gap: '16px', pt: '18px'}}>
              <PlaybooksNewName
                playbookTitle={playbookTitle}
                setPlaybookTitle={setPlaybookTitle}
              />
              <PlaybooksNewPrompt
                playbookDescription={playbookDescription}
                setPlaybookDescription={setPlaybookDescription}
              />
              <PlaybooksNewGroup
                existingGroup={existingGroup}
                groupMode={groupMode}
                groupOptions={groupOptions}
                newGroupName={newGroupName}
                setExistingGroup={setExistingGroup}
                setGroupMode={setGroupMode}
                setNewGroupName={setNewGroupName}
              />
            </Stack>
          }
          {
            activeTab === 'steps' &&
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'visible',
                gap: 0,
              }}
            >
              {
                steps.length > 0 &&
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    pr: '4px',
                    pt: '18px',
                  }}
                >
                  <PlaybooksStepsPreview
                    steps={steps}
                    defaultDay={playbookTotalDays}
                    setDefaultDay={setPlaybookTotalDays}
                    setSteps={setSteps}
                  />
                </Box>
              }
              <Box
                sx={{
                  flexShrink: 0,
                  pt: '18px',
                }}
              >
                <PlaybookStepPrompt
                  stepPrompt={stepPrompt}
                  setStepPrompt={setStepPrompt}
                  addStep={addStep}
                  defaultDay={playbookTotalDays}
                  setDefaultDay={setPlaybookTotalDays}
                />
              </Box>
            </Stack>
          }
          {
            activeTab === 'engagement' &&
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'visible',
                gap: 0,
              }}
            >
              {
                rules.length > 0 &&
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    pr: '4px',
                    pt: '18px',
                  }}
                >
                  <PlaybooksEngagementPreview
                    rules={rules}
                    setRules={setRules}
                  />
                </Box>
              }
              <Box
                sx={{
                  flexShrink: 0,
                  pt: '18px',
                }}
              >
                <PlaybookEngagementPrompt
                  rulePrompt={rulePrompt}
                  setRulePrompt={setRulePrompt}
                  addRule={addRule}
                />
              </Box>
            </Stack>
          }
        </Stack>

        <Box sx={{borderBottom: '1px solid #EEE8E2', flexShrink: 0}}></Box>

        <Stack
          direction='row'
          justifyContent='flex-end'
          sx={{
            flexShrink: 0,
            px: {
              xs: '20px',
              sm: '26px',
            },
            py: '12px',
            gap: '10px',
          }}
        >
          <Button
            onClick={onClose}
            variant='outlined'
            disableElevation
            disabled={isSubmitting}
            sx={{
              'borderRadius': '7px',
              'px': '10px',
              'py': '10px',
              'fontSize': '12px',
              'fontWeight': 400,
              'lineHeight': '100%',
              'textTransform': 'none',
              'color': '#6B6760',
              'borderColor': '#E8E4DE',
              'backgroundColor': '#FFFFFF',
              '&:hover': {
                backgroundColor: '#FFFFFF',
                borderColor: '#E8E4DE',
                boxShadow: 'none',
              },
            }}
          >
            <Translate expression='playbooks.New.Cancel' />
          </Button>
          <Button
            onClick={handlePrimaryAction}
            disableElevation
            disabled={primaryButtonDisabled}
            sx={{
              'borderRadius': '7px',
              'cursor': isSubmitting ? 'wait' : 'pointer',
              'px': '10px',
              'py': '10px',
              'fontSize': '12px',
              'fontWeight': 500,
              'lineHeight': '100%',
              'textTransform': 'none',
              'color': '#FFFFFF',
              'backgroundColor': '#DA7955',
              '&:hover': {
                backgroundColor: '#DA7955',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E8B19D',
                color: '#FFFFFF',
              },
            }}
          >
            <Translate expression={primaryButtonLabel} />
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

PlaybookNew.propTypes = {
  active: PropTypes.bool.isRequired,
  groupOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  handleCreatePlaybook: PropTypes.func.isRequired,
  playbook: PropTypes.object,
};

export default React.memo(PlaybookNew);
